import { observer } from 'mobx-react-lite';

import { YStack } from '@/components/ui/layout/layout';
import { Switch } from '@/components/ui/Switch';
import { pluginsStore, type PluginFlagState } from '@/plugins/plugins.store';

import { switchFeatureFlag } from './feature-flags';

const STATE_DESCRIPTIONS: Record<PluginFlagState, string> = {
  server: 'On for your account',
  'switched-on': 'On. You turned it on.',
  'switched-off': 'Off. You turned it off.',
  off: 'Off',
};

type PluginFlagListProps = {
  /** The developer panel's list: a heading over it, and each plugin's flag key under its name */
  showFlagKeys?: boolean;
  normalizedQuery?: string;
};

/** One switch per plugin folder, found the way the app finds them, so a new plugin shows here with no edit */
export const PluginFlagList = observer(
  ({ showFlagKeys = false, normalizedQuery = '' }: PluginFlagListProps) => {
    const visiblePlugins = pluginsStore.found.filter(
      (plugin) =>
        plugin.flagKey.includes(normalizedQuery) ||
        plugin.label.toLowerCase().includes(normalizedQuery)
    );
    if (visiblePlugins.length === 0) return null;

    return (
      <YStack className="py-1">
        {/* The released list opens from the Plugins button, so only the developer panel, which lists flags after it, needs a title */}
        {showFlagKeys && (
          <span className="px-3 py-1 font-medium text-foreground-muted">
            Plugins
          </span>
        )}
        {visiblePlugins.map((plugin) => (
          // A label, so a click anywhere on the row flips its switch
          <label
            key={plugin.id}
            className="mx-1 flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-surface-hover"
          >
            <YStack className="min-w-0 flex-1">
              <span className="truncate text-foreground">{plugin.label}</span>
              <span className="truncate text-foreground-subtle">
                {STATE_DESCRIPTIONS[plugin.state]}
              </span>
              {showFlagKeys && (
                <span className="truncate font-mono text-foreground-subtle">
                  {plugin.flagKey}
                </span>
              )}
            </YStack>
            <Switch
              aria-label={plugin.label}
              checked={
                plugin.state === 'server' || plugin.state === 'switched-on'
              }
              onCheckedChange={(enabled) =>
                switchFeatureFlag(plugin.flagKey, enabled)
              }
            />
          </label>
        ))}
        <p className="px-3 pt-1 text-foreground-subtle">
          Your switches apply only to you, on this computer. Some plugins also
          need the team to turn them on for your account, or parts of them
          won&apos;t load.
        </p>
      </YStack>
    );
  }
);
